import React, { useState, useEffect, useCallback } from 'react';
import { reportsAPI } from '../../services/garageApi';
import { formatCurrency } from '../../utils/helpers';
import BackButton from '../common/BackButton';

const SalesReport = () => {
    const [summary, setSummary] = useState(null);
    const [topProducts, setTopProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    const fetchSalesSummary = useCallback(async () => {
        try {
            setLoading(true);
            const params = {};
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;

            const response = await reportsAPI.salesSummary(params);
            setSummary(response.data.summary || null);
            setTopProducts(response.data.top_products || []);
        } catch (error) {
            console.error('Error fetching sales summary:', error);
        } finally {
            setLoading(false);
        }
    }, [dateFrom, dateTo]);

    useEffect(() => {
        fetchSalesSummary();
    }, [fetchSalesSummary]);

    const getProductName = (item) => {
        if (!item || !item.saleable) return '-';
        const product = item.saleable;
        return product.name || product.battery_name || product.service_name ||
            (product.size ? `${product.size}` : '-');
    };

    if (loading) {
        return <div className="flex justify-center items-center h-64">Loading report...</div>;
    }

    return (
        <div className="space-y-6">
            {/* Report Header */}
            <div className="bg-white shadow rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-3">
                        <BackButton />
                        <h2 className="text-2xl font-bold text-gray-900">Sales Summary Report</h2>
                    </div>
                    <div className="text-sm text-gray-500">
                        Generated: {new Date().toLocaleDateString()}
                    </div>
                </div>

                <div className="flex flex-wrap gap-4 mt-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">From Date</label>
                        <input
                            type="date"
                            value={dateFrom}
                            onChange={(e) => setDateFrom(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">To Date</label>
                        <input
                            type="date"
                            value={dateTo}
                            onChange={(e) => setDateTo(e.target.value)}
                            className="px-3 py-2 border border-gray-300 rounded-md"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white rounded-lg shadow p-6">
                        <p className="text-sm font-medium text-gray-600">Total Sales</p>
                        <p className="text-3xl font-bold text-gray-900">{summary.total_sales}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <p className="text-sm font-medium text-gray-600">Total Amount</p>
                        <p className="text-3xl font-bold text-gray-900">{formatCurrency(summary.total_amount)}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <p className="text-sm font-medium text-gray-600">Paid</p>
                        <p className="text-3xl font-bold text-green-600">{formatCurrency(summary.paid_amount)}</p>
                    </div>
                    <div className="bg-white rounded-lg shadow p-6">
                        <p className="text-sm font-medium text-gray-600">Pending</p>
                        <p className="text-3xl font-bold text-yellow-600">{formatCurrency(summary.pending_amount)}</p>
                        {parseFloat(summary.partial_amount) > 0 && (
                            <p className="text-sm text-orange-500 mt-1">
                                Partial: {formatCurrency(summary.partial_amount)}
                            </p>
                        )}
                    </div>
                </div>
            )}

            {/* Top Products Table */}
            {topProducts.length > 0 && (
                <div className="bg-white shadow rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">Top Selling Products</h3>
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Revenue</th>
                            </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                            {topProducts.map((item, index) => (
                                <tr key={index}>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{index + 1}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">{getProductName(item)}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                                        <span className="px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full capitalize">
                                            {item.saleable_type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">{formatCurrency(item.total)}</td>
                                </tr>
                            ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Print Button */}
            <div className="flex justify-end">
                <button
                    onClick={() => window.print()}
                    className="px-6 py-2 bg-gray-800 text-white rounded-md hover:bg-gray-900"
                >
                    Print Report
                </button>
            </div>
        </div>
    );
};

export default SalesReport;
